<template>
  <div class="policy-customer-form" id="policyForm">

    <!-- LOADER -->
    <div class="loader-container" v-if="!rendered">
      <custom-spinner></custom-spinner>
    </div>

    <!-- LOAD ERROR -->
    <div v-if="loadError" class="alert alert-danger">{{ loadError | translate('msg') }}</div>

    <template v-else-if="form && loaded">
      <div class="row" id="policyForm-row">
        <div class="col-9" id="policyForm-col">

          <!-- FORMIO FORM -->
          <formio ref="formRenderer" :form="form" :submission="submissionData" :language="'nl'" :options="{
            icons: 'fontawesome',
            i18n: { nl: formioLanguageNl },
            decimalSeparator: ',',
            thousandsSeparator: '.',
            readOnly: readOnly
          }" @render="onRender" @submit="submit" id="policyForm-formio"></formio>

        </div>
        <div class="col-3">

          <!-- ANCHORS -->
          <scrollactive class="anchors" :active-class="'active'" :offset="anchorsOffset" :modifyUrl="false"
            :highlightFirstItem="true" id="policyForm-anchors">
            <ul>
              <li v-for="(title, id, index) in formAnchors" :key="id">
                <a :href="'#' + id" class="scrollactive-item" :class="{ 'active': index === 0 }"
                  :id="'policyForm-anchors-' + title">{{ title }}</a>
              </li>
              <li>
                <a href="#" @click.prevent="goToTop" id="'policyForm-anchors-goTop-link">
                  {{ "TO_TOP" | translate }}
                  <i class="fa fa-chevron-up" aria-hidden="true" id="'policyForm-anchors-goTop"></i>
                </a>
              </li>
            </ul>
          </scrollactive>

        </div>
      </div>
    </template>
  </div>
</template>

<script>
import CustomSpinner from '../CustomSpinner';
import RestService from '../../service/RestService';
import GenericMapper from '../../service/mapper/GenericMapperFormio';
import FormConfigService from '../../service/FormConfigService';
import formioLanguageNl from '../../formio-language-nl.json';
import { Form } from 'vue-formio';

export default {
  props: ['policyId', 'formConfigType', 'readOnly', 'anchorsOffset'],
  components: {
    CustomSpinner,
    formio: Form
  },
  data: function () {
    return {
      rest: this.initRestService(),
      policies: {},
      form: null,
      formAnchors: {},
      mapper: null,
      submissionData: {
        data: {}
      },
      loaded: false,
      loadError: null,
      rendered: false,
      formioLanguageNl: formioLanguageNl,
      shouldSubmitFlag: false,
      originalFormData: null,
      _originalSnapTaken: false,
      localFormConfigType: '',
      lastChanges: {}
    }
  },
  async beforeMount() {
    this.localFormConfigType = this.formConfigType;
    this.form = await FormConfigService.ensureFormConfigIsLoadedAndParse(this.localFormConfigType);
    this.mapper = new GenericMapper();
    if (this.policyId) {
      await this.loadPolicy();
    } else {
      this.loaded = true;
    }
  },
  methods: {
    initRestService() {
      return new RestService();
    },
      // normalize all date-like strings in the entire object tree
    normalizeAllDates(obj) {
      if (Array.isArray(obj)) {
        return obj.map(v => this.normalizeAllDates(v));
      } else if (obj && typeof obj === 'object') {
        const out = {};
        Object.keys(obj).forEach(key => {
          out[key] = this.normalizeAllDates(obj[key]);
        });
        return out;
      } else if (typeof obj === 'string') {
        // If it's exactly 8 digits, assume YYYYMMDD and convert to YYYY-MM-DD
        if (/^\d{8}$/.test(obj)) {
          return obj.replace(/^(\d{4})(\d{2})(\d{2})$/, '$1-$2-$3');
        }
      }
      return obj;
    },
    diffObjects(a, b, path = '') {
      const changes = {};
      if (Array.isArray(a) && Array.isArray(b)) {
        const max = Math.max(a.length, b.length);
        for (let i = 0; i < max; i++) {
          const p = `${path}[${i}]`;
          if (i >= a.length) {
            changes[p] = { from: undefined, to: b[i] };
          } else if (i >= b.length) {
            changes[p] = { from: a[i], to: undefined };
          } else {
            Object.assign(changes, this.diffObjects(a[i], b[i], p));
          }
        }
        return changes;
      }
      if (a && typeof a === 'object' && !Array.isArray(a) &&
        b && typeof b === 'object' && !Array.isArray(b)) {
        const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
        for (const k of keys) {
          const p = path ? `${path}.${k}` : k;
          Object.assign(changes, this.diffObjects(a[k], b[k], p));
        }
        return changes;
      }
      if (JSON.stringify(a) !== JSON.stringify(b)) {
        changes[path] = { from: a, to: b };
      }
      return changes;
    },
    hasDataChanged() {
      const fr = this.$refs.formRenderer;
      let live = {};

      if (fr && fr.formio && fr.formio.submission && fr.formio.submission.data) {
        live = fr.formio.submission.data;
      } else if (this.submissionData && this.submissionData.data) {
        live = this.submissionData.data;
      }

      // Deep copies so we don't mutate live/original
      let currentRaw = JSON.parse(JSON.stringify(live));
      let originalRaw = JSON.parse(JSON.stringify(this.originalFormData || {}));

      // NEW: normalize dates everywhere
      currentRaw = this.normalizeAllDates(currentRaw);
      originalRaw = this.normalizeAllDates(originalRaw);

      const current = this.cleanObject(currentRaw);
      const original = this.cleanObject(originalRaw);

      if (current.map) current.map = this.normalizeCodeFields(current.map);
      if (original.map) original.map = this.normalizeCodeFields(original.map);

      const changes = this.diffObjects(original, current);
      this.lastChanges = changes;

      if (Object.keys(changes).length) {
        try {
          console.table(Object.entries(changes).map(([path, v]) => ({ path, from: v.from, to: v.to })));
        } catch (e) {}
      }
      return Object.keys(changes).length > 0;
    },
    cleanObject(obj) {
      if (Array.isArray(obj)) {
        return obj.map(v => this.cleanObject(v));
      } else if (obj && typeof obj === 'object') {
        // Sort keys for consistent comparison
        return Object.keys(obj).sort().reduce((acc, key) => {
          const value = this.cleanObject(obj[key]);
          // Only keep non-empty values
          if (
            value !== '' &&
            value !== null &&
            value !== undefined &&
            !(typeof value === 'object' && Object.keys(value).length === 0)
          ) {
            acc[key] = value;
          }
          return acc;
        }, {});
      }
      return obj;
    },
    normalizeCodeFields(map) {
      const normalized = { ...map };
      Object.keys(normalized).forEach(key => {
        if (key.endsWith('_CODE')) {
          // Convert empty arrays, null, or undefined to empty string
          if (
            normalized[key] === null ||
            normalized[key] === undefined ||
            (Array.isArray(normalized[key]) && normalized[key].length === 0)
          ) {
            normalized[key] = '';
          } else if (Array.isArray(normalized[key])) {
            if (normalized[key].length === 1) {
              normalized[key] = normalized[key][0];
            }
          } else if (typeof normalized[key] === 'object' && normalized[key] !== null) {
            const trueKeys = Object.keys(normalized[key]).filter(k => normalized[key][k]);
            normalized[key] = trueKeys.length === 1 ? trueKeys[0] : trueKeys;
          }
          // If after normalization the value is empty, delete the key
          if (normalized[key] === '' ||
            (Array.isArray(normalized[key]) && normalized[key].length === 0)) {
            delete normalized[key];
          }
        }
        // Convert numbers to strings for comparison
        if (typeof normalized[key] === 'number') {
          normalized[key] = normalized[key].toString();
        }
        // Normalize date fields to YYYY-MM-DD
        if (
          key.endsWith('DAT') || key.endsWith('DATE')
        ) {
          if (typeof normalized[key] === 'string') {
            // Convert YYYYMMDD to YYYY-MM-DD
            if (/^\d{8}$/.test(normalized[key])) {
              normalized[key] = normalized[key].replace(/^(\d{4})(\d{2})(\d{2})$/, '$1-$2-$3');
            }
          }
        }
      });
      return normalized;
    },
    async loadPolicy() {
      this.loadError = null;
      try {
        let response = await this.rest.getPolicyDetails(this.policyId);
        this.policies[this.policyId] = response.data;
        this.localFormConfigType = this.policies[this.policyId].productName + FormConfigService.typeEditPolicySuffix;
        this.form = await FormConfigService.ensureFormConfigIsLoadedAndParse(this.localFormConfigType);
        this.setSubmissionData();
      } catch (error) {
        this.loaded = true;
        this.loadError = 'ERROR_LOADING_DETAILS';
        this.$log.error(error.response.data);
      }
    },
    generateAnchors() {
      let anchorsObj = {};
      this.form.components
        .filter(component => component.type === 'panel')
        .forEach(component => { anchorsObj[component.id] = component.title });
      this.formAnchors = anchorsObj;
    },
    goToTop() {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },

    // UPDATED: snapshot original from live instance once, post-render
    onRender() {
      this.generateAnchors();
      this.rendered = true;
      this.$emit('form-rendered');

      this.$nextTick(() => {
        // one-time original snapshot after defaults/calcs
        const fr = this.$refs.formRenderer;
        const live = (fr && fr.formio && fr.formio.submission && fr.formio.submission.data)
          ? fr.formio.submission.data
          : null;

        if (live && !this._originalSnapTaken) {
          this.originalFormData = JSON.parse(JSON.stringify(live));
          this._originalSnapTaken = true;
        }
        this.formatDateFields(this.form.components);
      });
    },
    formatDateFields(components) {
      components.forEach(component => {
        const key = component.key.startsWith('map.') ? component.key.substring(4) : component.key;
        const nestedStructures = ['VZ', 'RP', 'CA', 'CL', 'VW'];
        nestedStructures.forEach(structure => {
          if (this.submissionData.data[structure]) {
            this.submissionData.data[structure].forEach(item => {
              const dateField = item.map[key];
              if (component.type === 'datetime' || component.apollonConverter === 'SUIVDate') {
                if (dateField) {
                  item.map[key] = this.reformatDateForFlatpickr(dateField);
                }
              }
            });
          }
        });
        // Handle date fields in the root 'map' object
        if (component.type === 'datetime' || component.apollonConverter === 'SUIVDate') {
          const dateField = this.submissionData.data.map[key];
          if (dateField) {
            this.submissionData.data.map[key] = this.reformatDateForFlatpickr(dateField);
          }
        }
        if (component.components) {
          this.formatDateFields(component.components);
        }
      });
    },
    reRenderForm() {
      this.rendered = false;
      this.loaded = false;
      setTimeout(() => { // used to force a re-render of the form
        this.loaded = true;
        this.rendered = true;
      }, 100);
    },
    setSubmissionData() {
      let data = this.mapper.preparePolicyForFormVisualization(this.policies[this.policyId], this.form);
      this.submissionData = { data: data };
      this.originalFormData = JSON.parse(JSON.stringify(data));
      this._originalSnapTaken = false; // allow the live snapshot after render
      this.reRenderForm();
    },
    setSubmitFlag(flag) {
      this.shouldSubmitFlag = flag;
    },
    reformatDateForFlatpickr(dateString) {
      if (dateString && dateString.length === 8) {
        const year = dateString.substring(0, 4);
        const month = dateString.substring(4, 6);
        const day = dateString.substring(6, 8);
        return `${year}-${month}-${day}`;
      }
      return dateString;
    },
    submit() {
      this.$refs['formRenderer'].formio.setPristine(false);
      const isFormValid = this.$refs['formRenderer'].formio.checkValidity();
      if (isFormValid) {
        // Work on a deep copy so we never mutate Formio's live model
        const working = JSON.parse(JSON.stringify(this.submissionData));
        // Check if VZ exists and flatten it into map
        this.processField(working, 'VZ');
        this.processField(working, 'RP');
        this.processField(working, 'CA');
        this.processField(working, 'CL');
        this.processField(working, 'VW');
        // Modify POLICYHOLDER dateOfBirth if it exists
        if (working.data.persons != null && working.data.persons.POLICYHOLDER != null) {
          let dOB = working.data.persons.POLICYHOLDER.dateOfBirth;
          if (dOB) {
            let dOBDate = new Date(dOB);
            dOBDate.setHours(dOBDate.getHours() + 2);
            let modifiedDOB = dOBDate.toJSON();
            working.data.persons.POLICYHOLDER.dateOfBirth = modifiedDOB;
          }
        }
        // Emit form submit with transformed data
        this.$emit('form-submit', this.mapper.buildRequest(working.data, this.form));
      } else {
        this.$emit('form-invalid');
        this.$notify({
          type: 'alert-danger',
          duration: -1,
          text: this.$options.filters.translate('FORM_INVALID_PLEASE_FILL', 'error')
        });
      }
    },
    processField(submissionData, fieldName) {
      if (Array.isArray(submissionData.data[fieldName]) && submissionData.data[fieldName].length > 0) {
        const fieldMap = submissionData.data.map;
        Object.keys(fieldMap).forEach(key => {
          if (key.startsWith(`${fieldName}_`)) {
            delete fieldMap[key];
          }
        });
        submissionData.data[fieldName].forEach((fieldItem, index) => {
          const fieldMap = fieldItem.map;

          if (fieldMap) {
            // Handle dynamically for both RP_CODE and CA_CODE
            const codeFieldName = `${fieldName}_CODE`;
            if (fieldMap[codeFieldName]) {
              const processedCodeData = [];
              Object.keys(fieldMap[codeFieldName]).forEach(key => {
                if (fieldMap[codeFieldName][key] === true) {
                  processedCodeData.push(`${codeFieldName}[${index}]:${key}`);
                }
              });

              // Integrate the processed _CODE data
              processedCodeData.forEach(item => {
                const [key, value] = item.split(':');
                if (value) {
                  submissionData.data.map[key] = value;
                }
              });
            }

            // Dynamically map all other fields with index notation
            Object.keys(fieldMap).forEach(key => {
              if (key !== codeFieldName) { // Avoid double processing of _CODE fields
                submissionData.data.map[`${key}[${index}]`] = fieldMap[key];
              }
            });
          }
        });

        // Optionally, remove the processed field
        delete submissionData.data[fieldName];
      }
    }

  },
  watch: {
    async policyId(newValue) {
      await this.loadPolicy();
      this.setSubmissionData();
      if (this.shouldSubmitFlag) {
        this.submit();
        this.shouldSubmitFlag = false;
      }
      this.reRenderForm();
    },
    readOnly() {
      this.reRenderForm();
    },
    formConfigType(newFormConfigType) {
      // Update the localFormConfigType whenever the prop value changes
      this.localFormConfigType = newFormConfigType;
      // Additional logic or actions if needed
    },
    localFormConfigType(newLocalFormConfigType) {
      // Perform actions based on the modified localFormConfigType value
      // For example, you might emit an event to notify the parent component about the change
      this.$emit('update:formConfigType', newLocalFormConfigType);
    }
  }
}
</script>

<style lang="scss" scoped>
/* LOADER */
.loader-container {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  padding-top: 80px;
  text-align: center;
  background: rgba(255, 255, 255, .5);
  z-index: 4;
}

.policy-customer-form {
  position: relative;
  min-height: 100vh;
}

>>>.card {
  margin-bottom: 1.5rem !important;
}

>>>.form-control,
>>>.input-group-text {
  border-radius: 0;
}

>>>.form-control[disabled] {
  background-color: #e8e8e8;
}

>>>.custom-validation-error {
  margin-top: .25rem;
  font-size: 80%;
  color: #dc3545;
}

.anchors {
  position: fixed;
  /* old browsers (IE11) support */
  position: sticky;
  top: 76px;
  /* .navbar height + 20px */
}

.anchors ul {
  padding: 0;
  list-style: none;
}

.anchors a {
  padding: 8px 0;
  display: inline-block;
  font-size: 1.1rem;
  line-height: 1.3;
  color: #000;
}

.anchors a.active {
  font-weight: 500;
}

.anchors li:last-child a {
  margin-top: 10px;
  font-size: 1rem;
  font-weight: 500;
}

.anchors li:last-child a i {
  margin-left: 4px;
  font-size: .6rem;
  transform: translateY(-2px);
}
</style>



// WEBPACK FOOTER //
// src/components/case/PolicyForm.vue